import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Search, Plus, CalendarClock, Gauge, Check, AlertTriangle, Truck, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';

/**
 * The service plan: what each truck is due for, before it breaks.
 *
 * Everything else on this page answers "what went wrong". This answers "what
 * is about to", which is the only half that saves money. It was also the half
 * that had never been filled in — seventy-one trucks, not one scheduled
 * service between them, which is why the Upcoming tab has always been empty
 * and the "oil due soon" tile has always read zero.
 *
 * A schedule is two numbers and a starting point: service every N kilometres
 * or every N days, counted from the last time it was done. Postgres computes
 * the due point from those (next_due_km and next_due_date are generated
 * columns), so nothing here works out a due date itself — it saves the
 * baseline and reads back what the database derived.
 *
 * The baseline is the part people skip, and a schedule without one never comes
 * due. So "start from the standard plan" fills it in from the truck's current
 * odometer and today's date, which is honest: it says "from here on", not "we
 * pretended to know when this was last serviced".
 */

interface ScheduleRow {
  id: string;
  vehicle_id: string;
  event_type: string;
  interval_km: number | null;
  interval_days: number | null;
  last_done_km: number | null;
  last_done_date: string | null;
  next_due_km: number | null;
  next_due_date: string | null;
  is_active: boolean;
  vehicle_plate: string | null;
  vehicle_code: string | null;
  current_odometer_km: number | null;
  km_remaining: number | null;
  days_remaining: number | null;
}

interface Vehicle {
  id: string;
  code: string;
  plateEn: string | null;
  currentOdometerKm: number | null;
  status: string;
}

interface Coverage {
  total: number;
  planned: number;
  unplanned: number;
  no_odometer: number;
}

interface BaselineResult {
  baselined: number;
  linesCreated: number;
  distanceLinesSkippedForMissingOdometer: number;
  vehiclesWithoutOdometer: Array<string | null>;
}

/**
 * A starting point for a heavy tipper, not a rule. Every fleet argues with
 * these numbers and every fleet should — they are here so the first schedule
 * takes one press instead of thirty, and every one of them is editable before
 * it is saved.
 */
const STANDARD_PLAN: Array<{ type: string; km?: number; days?: number }> = [
  { type: 'oil_change', km: 10000, days: 180 },
  { type: 'oil_filter', km: 10000, days: 180 },
  { type: 'fuel_filter', km: 20000, days: 365 },
  { type: 'air_filter', km: 20000, days: 365 },
  { type: 'brake_pads_front', km: 40000 },
  { type: 'tire_rotation', km: 10000 },
  { type: 'general_service', km: 20000, days: 180 },
  { type: 'periodic_inspection', days: 365 },
];

/** One editable line, before it is saved. */
interface Draft {
  eventType: string;
  intervalKm: string;
  intervalDays: string;
  lastDoneKm: string;
  lastDoneDate: string;
}

const today = () => new Date().toISOString().slice(0, 10);

export function ServicePlanPanel({ typeLabel, canEdit }: {
  typeLabel: (t: string) => string;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [vehicleId, setVehicleId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);

  const vehicles = useQuery({
    queryKey: ['plan-vehicles'],
    queryFn: async () =>
      (await apiClient.get<{ items: Vehicle[] }>('/api/v1/vehicles', {
        params: { pageSize: 500, status: 'active' },
      })).data.items ?? [],
  });

  const schedules = useQuery({
    queryKey: ['maintenance-schedule'],
    queryFn: async () =>
      (await apiClient.get<ScheduleRow[]>('/api/v1/maintenance/schedule')).data ?? [],
  });

  const coverage = useQuery({
    queryKey: ['maintenance-schedule-coverage'],
    queryFn: async () =>
      (await apiClient.get<Coverage>('/api/v1/maintenance/schedule/coverage')).data,
  });

  /**
   * One press for the whole fleet. Setting seventy-one trucks up by hand is
   * work nobody was ever going to finish, which is why none of them had a plan.
   * Trucks that already have one are untouched.
   */
  const baseline = useMutation({
    mutationFn: async () =>
      (await apiClient.post<BaselineResult>('/api/v1/maintenance/schedule/baseline-fleet', {})).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['maintenance-schedule'] });
      void qc.invalidateQueries({ queryKey: ['maintenance-schedule-coverage'] });
    },
  });

  const save = useMutation({
    mutationFn: async (d: Draft) =>
      apiClient.post('/api/v1/maintenance/schedule', {
        vehicleId,
        eventType: d.eventType,
        intervalKm: d.intervalKm ? Number(d.intervalKm) : undefined,
        intervalDays: d.intervalDays ? Number(d.intervalDays) : undefined,
        lastDoneKm: d.lastDoneKm ? Number(d.lastDoneKm) : undefined,
        lastDoneDate: d.lastDoneDate || undefined,
      }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['maintenance-schedule'] }); },
  });

  const retire = useMutation({
    mutationFn: async (id: string) =>
      apiClient.patch(`/api/v1/maintenance/schedule/${id}`, { isActive: false }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['maintenance-schedule'] }); },
  });

  const byVehicle = useMemo(() => {
    const m = new Map<string, ScheduleRow[]>();
    for (const r of schedules.data ?? []) {
      const list = m.get(r.vehicle_id) ?? [];
      list.push(r);
      m.set(r.vehicle_id, list);
    }
    return m;
  }, [schedules.data]);

  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (vehicles.data ?? [])
      .filter((v) => !q || (v.plateEn ?? '').toLowerCase().includes(q) || v.code.toLowerCase().includes(q))
      .sort((a, b) => {
        // Trucks with no plan first: they are the work.
        const an = byVehicle.has(a.id) ? 1 : 0;
        const bn = byVehicle.has(b.id) ? 1 : 0;
        if (an !== bn) return an - bn;
        return (a.plateEn ?? a.code).localeCompare(b.plateEn ?? b.code);
      });
  }, [vehicles.data, search, byVehicle]);

  const vehicle = (vehicles.data ?? []).find((v) => v.id === vehicleId) ?? null;
  const rows = vehicleId ? (byVehicle.get(vehicleId) ?? []) : [];
  const planned = byVehicle.size;
  const total = vehicles.data?.length ?? 0;

  /** Numeric fields hold digits only; the server takes numbers, not "10,000 km". */
  const digits = (s: string) => s.replace(/[^0-9]/g, '');
  const patch = (i: number, fields: Partial<Draft>) =>
    setDrafts((prev) => prev.map((x, j) => (j === i ? { ...x, ...fields } : x)));

  const startStandard = () => {
    if (!vehicle) return;
    const odo = vehicle.currentOdometerKm ?? 0;
    const have = new Set(rows.map((r) => r.event_type));
    setDrafts(
      STANDARD_PLAN.filter((p) => !have.has(p.type)).map((p) => ({
        eventType: p.type,
        intervalKm: p.km ? String(p.km) : '',
        intervalDays: p.days ? String(p.days) : '',
        // Counted from here on, not from a date nobody recorded.
        lastDoneKm: p.km ? String(odo) : '',
        lastDoneDate: p.days ? today() : '',
      })),
    );
    setSaveError(null);
  };

  /**
   * Saved one line at a time, and stopped at the first refusal rather than
   * pressed on: if the server rejects an interval, the remaining lines stay on
   * screen with what was typed in them, so the fix is an edit and not a
   * re-entry. mutateAsync is awaited directly because the mutation's own
   * isError flag is a render away and would still be false inside this loop.
   */
  const saveAll = async () => {
    setSaveError(null);
    const pending = drafts.filter((d) => d.intervalKm || d.intervalDays);
    const done = new Set<string>();
    for (const d of pending) {
      try {
        await save.mutateAsync(d);
        done.add(d.eventType);
      } catch (err) {
        setSaveError(extractErrorMessage(err));
        setDrafts((prev) => prev.filter((x) => !done.has(x.eventType)));
        return;
      }
    }
    setDrafts([]);
  };

  const standing = (r: ScheduleRow) => {
    const dueByDate = r.days_remaining !== null && r.days_remaining <= 0;
    const dueByKm = r.km_remaining !== null && r.km_remaining <= 0;
    if (dueByDate || dueByKm) return { tone: 'rose', label: 'Overdue' };
    const soonDate = r.days_remaining !== null && r.days_remaining <= 14;
    const soonKm = r.km_remaining !== null && r.km_remaining <= 1000;
    if (soonDate || soonKm) return { tone: 'amber', label: 'Due soon' };
    if (r.next_due_km === null && r.next_due_date === null) {
      return { tone: 'muted', label: 'No baseline' };
    }
    return { tone: 'emerald', label: 'In window' };
  };

  if (vehicles.isLoading || schedules.isLoading) {
    return <div className="flex items-center justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-3">
      {/* Where the fleet actually stands. Said plainly, because zero of
          seventy-one is not a number to bury in a tooltip. */}
      <div className={'rounded-lg border p-3 ' + ((coverage.data?.unplanned ?? 0) > 0
        ? 'border-amber-500/40 bg-amber-500/5'
        : 'border-border bg-card')}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-medium inline-flex items-center gap-2">
              <CalendarClock className="h-4 w-4" />
              {coverage.data
                ? coverage.data.unplanned > 0
                  ? `${coverage.data.unplanned} vehicle${coverage.data.unplanned === 1 ? '' : 's'} missing an active service plan`
                  : `All ${coverage.data.total} active vehicles have a service plan`
                : `${planned} of ${total} trucks have a service plan`}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              A plan is what makes a service arrive before the breakdown does. Until one exists,
              Due soon stays empty and every job on this page is a repair rather than a service.
            </p>
          </div>

          {canEdit && (coverage.data?.unplanned ?? 0) > 0 && (
            <Button size="sm" variant="gold" className="gap-1.5 shrink-0"
              disabled={baseline.isPending}
              onClick={() => baseline.mutate()}>
              <CalendarClock className="h-3.5 w-3.5" />
              {baseline.isPending ? 'Applying…' : 'Apply standard fleet baseline'}
            </Button>
          )}
        </div>

        {/* What one press actually did — said in full, including what it could
            not do, because a silent partial success is how a fleet ends up
            believing it is covered when part of it is not. */}
        {baseline.data && (
          <div className="mt-3 rounded-md border border-border bg-background p-2.5 text-xs">
            <div className="font-medium text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5" />
              {baseline.data.baselined} vehicle{baseline.data.baselined === 1 ? '' : 's'} baselined
              {' · '}{baseline.data.linesCreated} service line{baseline.data.linesCreated === 1 ? '' : 's'} created
            </div>
            <p className="text-muted-foreground mt-1">
              Counted from today and each truck&rsquo;s current odometer — a starting point, not a recorded history.
            </p>
            {baseline.data.vehiclesWithoutOdometer.length > 0 && (
              <p className="text-amber-600 dark:text-amber-400 mt-1.5">
                {baseline.data.vehiclesWithoutOdometer.length} vehicle
                {baseline.data.vehiclesWithoutOdometer.length === 1 ? ' has' : 's have'} no odometer reading, so
                {' '}{baseline.data.distanceLinesSkippedForMissingOdometer} distance-based service
                {baseline.data.distanceLinesSkippedForMissingOdometer === 1 ? '' : 's'} could not be set:
                {' '}{baseline.data.vehiclesWithoutOdometer.filter(Boolean).join(', ') || 'unnamed'}.
                Their date-based services are in place.
              </p>
            )}
          </div>
        )}
        {baseline.error && (
          <p className="text-xs text-rose-600 dark:text-rose-400 mt-2">{extractErrorMessage(baseline.error)}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-3">
        {/* Pick a truck. */}
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <div className="p-2 border-b border-border relative">
            <Search className="absolute start-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Plate or code" className="ps-9 h-9" />
          </div>
          <div className="max-h-[26rem] overflow-y-auto">
            {list.map((v) => {
              const count = byVehicle.get(v.id)?.length ?? 0;
              return (
                <button key={v.id} type="button"
                  onClick={() => { setVehicleId(v.id); setDrafts([]); setSaveError(null); }}
                  className={'w-full text-start px-3 py-2 border-b border-border/60 last:border-0 hover:bg-muted/40 transition-colors ' +
                    (vehicleId === v.id ? 'bg-muted/60' : '')}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs" dir="ltr">{v.plateEn ?? v.code}</span>
                    {count > 0 ? (
                      <span className="text-[10px] text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1">
                        <Check className="h-3 w-3" />{count}
                      </span>
                    ) : (
                      <span className="text-[10px] text-amber-600 dark:text-amber-400">no plan</span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {v.currentOdometerKm ? `${Number(v.currentOdometerKm).toLocaleString('en-US')} km` : 'no odometer'}
                  </div>
                </button>
              );
            })}
            {list.length === 0 && (
              <div className="px-3 py-8 text-center text-xs text-muted-foreground">No truck matches.</div>
            )}
          </div>
        </div>

        {/* Its plan. */}
        <div className="rounded-lg border border-border bg-card p-3 space-y-3">
          {!vehicle ? (
            <div className="py-12 text-center">
              <Truck className="h-8 w-8 text-muted-foreground mx-auto mb-2 opacity-30" />
              <p className="text-sm text-muted-foreground">Choose a truck to see or set its plan.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <div className="font-mono text-sm" dir="ltr">{vehicle.plateEn ?? vehicle.code}</div>
                  <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                    <Gauge className="h-3 w-3" />
                    {vehicle.currentOdometerKm
                      ? `${Number(vehicle.currentOdometerKm).toLocaleString('en-US')} km on the clock`
                      : 'no odometer reading — a distance schedule cannot be measured'}
                  </div>
                </div>
                {canEdit && drafts.length === 0 && (
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={startStandard}>
                    <Plus className="h-3.5 w-3.5" />
                    {rows.length === 0 ? 'Start from the standard plan' : 'Add the missing services'}
                  </Button>
                )}
              </div>

              {/* Saved lines. */}
              {rows.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-muted-foreground border-b border-border">
                      <tr>
                        <th className="text-start py-2 pe-2 font-medium">Service</th>
                        <th className="text-end py-2 px-2 font-medium">Every</th>
                        <th className="text-end py-2 px-2 font-medium">Last done</th>
                        <th className="text-end py-2 px-2 font-medium">Next due</th>
                        <th className="text-start py-2 ps-2 font-medium">Standing</th>
                        {canEdit && <th className="w-8" />}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const st = standing(r);
                        const tone = st.tone === 'rose'
                          ? 'border-rose-500 text-rose-600 dark:text-rose-400'
                          : st.tone === 'amber'
                            ? 'border-amber-500/50 text-amber-600 dark:text-amber-400'
                            : st.tone === 'emerald'
                              ? 'border-emerald-500/40 text-emerald-600 dark:text-emerald-400'
                              : 'border-border text-muted-foreground';
                        return (
                          <tr key={r.id} className="border-b border-border/60 last:border-0">
                            <td className="py-2 pe-2">{typeLabel(r.event_type)}</td>
                            <td className="py-2 px-2 text-end text-xs text-muted-foreground whitespace-nowrap">
                              {[r.interval_km ? `${r.interval_km.toLocaleString('en-US')} km` : null,
                                r.interval_days ? `${r.interval_days} days` : null]
                                .filter(Boolean).join(' or ') || '—'}
                            </td>
                            <td className="py-2 px-2 text-end text-xs font-mono text-muted-foreground whitespace-nowrap">
                              {[r.last_done_km ? `${r.last_done_km.toLocaleString('en-US')} km` : null, r.last_done_date]
                                .filter(Boolean).join(' · ') || '—'}
                            </td>
                            <td className="py-2 px-2 text-end text-xs font-mono whitespace-nowrap">
                              {[r.next_due_km ? `${r.next_due_km.toLocaleString('en-US')} km` : null, r.next_due_date]
                                .filter(Boolean).join(' · ') || '—'}
                            </td>
                            <td className="py-2 ps-2">
                              <span className={`text-[11px] rounded-full border px-2 py-0.5 whitespace-nowrap ${tone}`}>
                                {st.label}
                              </span>
                            </td>
                            {canEdit && (
                              <td className="py-2">
                                <button type="button" title="Remove from the plan"
                                  onClick={() => retire.mutate(r.id)}
                                  className="text-muted-foreground hover:text-rose-600 dark:hover:text-rose-400">
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {rows.length === 0 && drafts.length === 0 && (
                <div className="py-8 text-center">
                  <AlertTriangle className="h-7 w-7 text-amber-500 mx-auto mb-2 opacity-60" />
                  <p className="text-sm text-muted-foreground">
                    This truck has no scheduled service. Nothing will ever appear under Upcoming for it.
                  </p>
                </div>
              )}

              {/* Lines being written. */}
              {drafts.length > 0 && (
                <div className="rounded-lg border border-border p-2 space-y-2">
                  <div className="text-xs text-muted-foreground">
                    Adjust anything that is wrong for this truck, then save. Counted from the
                    reading and date below — a service is due from the last one, not from nothing.
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-[11px] text-muted-foreground">
                        <tr>
                          <th className="text-start py-1 pe-2 font-medium">Service</th>
                          <th className="text-start py-1 px-1 font-medium">Every km</th>
                          <th className="text-start py-1 px-1 font-medium">Every days</th>
                          <th className="text-start py-1 px-1 font-medium">Last done km</th>
                          <th className="text-start py-1 px-1 font-medium">Last done date</th>
                          <th className="w-8" />
                        </tr>
                      </thead>
                      <tbody>
                        {drafts.map((d, i) => (
                          <tr key={d.eventType}>
                            <td className="py-1 pe-2 whitespace-nowrap">{typeLabel(d.eventType)}</td>
                            <td className="py-1 px-1">
                              <input inputMode="numeric" value={d.intervalKm}
                                onChange={(ev) => patch(i, { intervalKm: digits(ev.target.value) })}
                                className="w-24 h-8 rounded-md border border-input bg-background px-2 text-sm" />
                            </td>
                            <td className="py-1 px-1">
                              <input inputMode="numeric" value={d.intervalDays}
                                onChange={(ev) => patch(i, { intervalDays: digits(ev.target.value) })}
                                className="w-24 h-8 rounded-md border border-input bg-background px-2 text-sm" />
                            </td>
                            <td className="py-1 px-1">
                              <input inputMode="numeric" value={d.lastDoneKm}
                                onChange={(ev) => patch(i, { lastDoneKm: digits(ev.target.value) })}
                                className="w-24 h-8 rounded-md border border-input bg-background px-2 text-sm" />
                            </td>
                            <td className="py-1 px-1">
                              <input type="date" value={d.lastDoneDate}
                                onChange={(ev) => patch(i, { lastDoneDate: ev.target.value })}
                                className="h-8 rounded-md border border-input bg-background px-2 text-sm" />
                            </td>
                            <td className="py-1">
                              <button type="button" onClick={() => setDrafts((prev) => prev.filter((_, j) => j !== i))}
                                className="text-muted-foreground hover:text-foreground text-xs">✕</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="gold" disabled={save.isPending} onClick={saveAll}>
                      {save.isPending ? 'Saving…' : `Save ${drafts.length} service${drafts.length === 1 ? '' : 's'}`}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setDrafts([]); setSaveError(null); }}>Discard</Button>
                  </div>
                  {saveError && <p className="text-xs text-rose-600 dark:text-rose-400">{saveError}</p>}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
